<template>
    <modal
        @closed="closeModal"
        :allowCancel="false">
        <div class="bumper-box-modal">
            <h2 v-if="selectedBox">{{upgradeModelHeading}}</h2>
            <p v-if="isBumper">{{$t('BUMPER_BOX_UPGRADE_MODAL_TEXT', { theBox: bumperBoxName })}}</p>
            <p v-if="isBumper">{{$t('PER_BOX', {price: selectedBoxPrice})}}</p>
            <p class="bumper-box-modal--link" v-if="showMoreInfoLink && isBumper" @click="goToBumperBoxPage">{{$t('BUMPER_BOX_UPGRADE_MODAL_FIND_MORE')}}</p>
            <subscription-dropdown
                v-if="showDropdown"
                v-model="selectedAddress"
                label="Select account to upgrade"
                class="bumper-box-modal--dropdown"/>
            <div class="bumper-box-modal--select">
                <div class="bumper-box-modal--select--one background--paper"
                    :class="{'inActive': allSelected}"
                    @click="allSelected = false" v-if="isBumper">
                    <h2>{{$t('BUMPER_BOX_UPGRADE_MODAL_THIS_ONE')}}</h2>
                    <div class="bumper-box-modal--select--image">
                        <img class="image" src="@/assets/images/user-menu/your-boxes.svg" alt="Simgle box image">
                    </div>
                    <p>{{$t('BUMPER_BOX_UPGRADE_MODAL_ONE_DELIVERY', { theBox: bumperBoxName })}}</p>
                    <div class="dropdown" :class="{'noClick': allSelected}">
                        <multiselect
                            v-model="selectedBumperDelivery"
                            :track-by="'human'"
                            :label="'human'"
                            :placeholder="'Choose a delivery date'"
                            :allow-empty="false"
                            openDirection="below"
                            :options="allowedDeliveries" />
                    </div>
                </div>
                <div class="bumper-box-modal--select--all background--paper"  :class="{'inActive': !allSelected}" @click="allSelected = true">
                    <h2 v-if="selectedBox">{{upgradeModalForeverText}}</h2>
                    <div class="bumper-box-modal--select--image">
                        <img class="image" src="@/assets/images/user-menu/BOXES.png" alt="multi box image">
                    </div>
                    <p v-if="selectedBox">{{upgradeModalUpgradeAll}}</p>
                </div>
            </div>
            <p v-if="!allSelected">{{$t('BUMPER_BOX_UPGRADE_MODAL_NO_VASE', { theBox: bumperBoxName })}}</p>
            <p v-if="errorMessage && !selectedBumperDelivery && !allSelected" class="error">
                {{errorMessage}}
            </p>
        </div>
        <submit-button
            slot="actions-prefix"
            color="primary"
            @click="upgradeBox"
            :processing="isProcessing"
            class="accept">{{ $t('BUMPER_BOX_UPGRADE_MODAL_CONFIRM_CTA') }}</submit-button>
        <submit-button
            slot="actions-suffix"
            color="secondary"
            @click="closeModal"
            class="reject">{{ $t('BUMPER_BOX_UPGRADE_MODAL_CANCEL_CTA') }}</submit-button>
    </modal>
</template>

<script>
import { mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import { formatDate } from '@/helpers/DateHelpers';
import * as Dates from '@/constants/Dates';
import Modal from '@/components/structure/Modal';
import SubmitButton from '@/components/forms/buttons/SubmitButton';
import SubscriptionDropdown from '@/components/partials/account/rate-my-flowers/SubscriptionDropdown';
import { PRODUCT_FAMILY_BUMPER, PRODUCT_FAMILY_STANDARD, PRODUCT_FAMILY_LITE, DISPLAY_NAME_BUMPER } from '@/constants/Gifting';
import { getBoxPrice } from '@/helpers/QueryHelpers';
import { CSPageEvents } from '@/helpers/GAHelpers';

export default {
    name: 'BumperBoxUpgradeoModal',
    props: {
        subscription: {
            type: Object,
            default: null,
            required: false,
        },
        showMoreInfoLink: {
            type: Boolean,
            default: true,
            required: false,
        },
        selectedBox: {
            type: Object,
            default: () => {},
        },
    },
    components: {
        Modal,
        SubmitButton,
        SubscriptionDropdown,
        Multiselect,
    },
    mounted() {
        if (!this.primaryProducts.length) this.$store.dispatch('subscription/fetchAvailableDeliveryDay', this.address.postcode);
    },
    data() {
        return {
            isProcessing: false,
            selectedAddress: null,
            selectedBumperDelivery: null,
            allSelected: true,
            errorMessage: null,
            bumperBoxName: DISPLAY_NAME_BUMPER,
            bumperBoxPrice: process.env.VUE_APP_BUMPER_BOX_PRICE,
            liteBoxPrice: process.env.VUE_APP_LITE_BOX_PRICE,
        };
    },
    computed: {
        ...mapGetters({
            activeSubscriptions: 'subscription/getActiveSubscriptions',
            activeSubscription: 'subscription/getActiveSubscription',
            primaryProducts: 'subscription/getProductTypes',
            getAddressById: 'address/getAddressById',
            deliveries: 'scheduledOrder/getDeliveries',
            boxPrice: 'register/getBoxPrice',
            deliveryPrice: 'register/getDeliveryPrice',
        }),
        upgradeModelHeading() {
            if (this.isLite) {
                return this.$t('BUMPER_BOX_LITE_MODAL_HEADING');
            } else if (this.isStandard) {
                return this.$t('BUMPER_BOX_DOWNGRADE_MODAL_HEADING');
            } else {
                return this.$t('BUMPER_BOX_UPGRADE_MODAL_HEADING');
            }
        },
        upgradeModalForeverText() {
            if (this.isLite) {
                return this.$t('BUMPER_BOX_LITE_MODAL_FOREVER');
            } else if (this.isStandard) {
                return this.$t('BUMPER_BOX_DOWNGRADE_MODAL_FOREVER');
            } else {
                return this.$t('BUMPER_BOX_UPGRADE_MODAL_FOREVER');
            }
        },
        upgradeModalUpgradeAll() {
            if (this.isLite) {
                return this.$t('BUMPER_BOX_LITE_MODAL_UPGRADE_ALL', { theBox: DISPLAY_NAME_BUMPER });
            } else if (this.isStandard) {
                return this.$t('BUMPER_BOX_DOWNGRADE_MODAL_UPGRADE_ALL', { theBox: DISPLAY_NAME_BUMPER });
            } else {
                return this.$t('BUMPER_BOX_UPGRADE_MODAL_UPGRADE_ALL', { theBox: DISPLAY_NAME_BUMPER });
            }
        },
        isLite() {
            return this.selectedBox?.product_family === PRODUCT_FAMILY_LITE;
        },
        isStandard() {
            return this.selectedBox?.product_family === PRODUCT_FAMILY_STANDARD;
        },
        isBumper() {
            return this.selectedBox?.product_family === PRODUCT_FAMILY_BUMPER;
        },
        selectedBoxPrice() {
            return this.selectedBox?.price;
        },
        standardBoxPrice() {
            return getBoxPrice(this.boxPrice, this.deliveryPrice);
        },
        activeProduct() {
            return this.selectedSubscription.product_family;
        },
        showDropdown() {
            return this.subscription === null && this.activeSubscriptions.length > 1;
        },
        selectedSubscription() {
            if (this.subscription) return this.subscription;
            return this.activeSubscription;
        },
        selectedBoxName() {
            return this.selectedBox.product_family;
        },
        selectedBoxId() {
            return this.selectedBox.id;
        },
        address() {
            return this.getAddressById(this.selectedSubscription.delivery_address_id) || {};
        },
        allowedDeliveries() {
            return this.deliveries.filter((item) => !item.is_locked && !item.scheduled_order_id)
                .map((item) => {
                    item.human = formatDate(new Date(item.datetime), Dates.UK_DATE_FORMAT);
                    return item;
                });
        },
        isCalendarPage() {
            return this.$route.name === 'account.delivery-schedule';
        },
    },
    methods: {
        closeModal() {
            this.isProcessing = false;
            this.$emit('closed');
        },
        upgradeBox() {
            if (this.selectedBoxName === PRODUCT_FAMILY_STANDARD) {
                CSPageEvents('standard-box-downgrade-all');
            } else {
                this.allSelected ? CSPageEvents('bumper-box-upgrade-all') : CSPageEvents('bumper-box-upgrade-Single');
            }

            if (!this.allSelected && !this.selectedBumperDelivery) {
                this.errorMessage = 'Please select a delivery date';
                return;
            }
            this.errorMessage = null;
            this.isProcessing = true;
            let date = this.allSelected ? null : this.selectedBumperDelivery.datetime;
            this.$store.dispatch('subscription/updateProductType', { subId: this.selectedSubscription?.id, prodId: this.selectedBoxId, name: this.selectedBoxName, date })
                .then(() => {
                    this.$store.dispatch('scheduledOrder/fetchAllDeliveries', true);
                    this.closeModal();
                    !this.isCalendarPage && this.$router.push({ name: 'account.delivery-schedule' });
                })
                .catch((error) => {
                    this.errorMessage = error.message;
                    this.isProcessing = false;
                });
        },
        goToBumperBoxPage() {
            this.$router.push({ name: 'account.bumper-box' });
        },
    },
};
</script>

<style lang="scss" scoped>
.bumper-box-modal {
    margin: 10px auto;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    max-width: 600px;
    &--select {
        display:flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        @media (max-width: 640px) {
            flex-direction: column-reverse;
        }
        &--image {
            width: 150px;
            height: 100px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-image: url('~@/assets/images/decoration/bg1.svg');
            background-repeat: no-repeat;
            background-position: center;
            background-size: contain;
            & .image {
                width: 60%;
            }
        }
        &--one, &--all {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            width: 270px;
            cursor: pointer;
            padding: 10px 40px;
            background: #d9e6cc;
            border-radius: 4px;
            transition: transform 0.3s ease-in-out;
            p {
                padding: 10px 20px;
            }
        }
    }
    &--dropdown {
        width: 100%;
        margin: 20px auto;
        text-align: center;
        width: 50%;
        min-width: 200px;
    }
    &--link {
        cursor: pointer;
        text-decoration: underline;
    }
}
.accept, .reject {
    width: 200px;
    margin: 10px 30px;
}
.btn {
    min-width: 150px;
}
.inActive {
    transform: scale(0.8);
    transition: transform 0.3s ease-in-out;
    opacity: 0.5;
    color: #1A1A1A;
    background: #f2f2f2;
    .bumper-box-modal--select--image {
        background: none;
    }

}
.noClick {
    pointer-events: none;
}

.dropdown{
    width: 250px;
}

</style>
